import type { MouseEvent as ReactMouseEvent } from "react";

import { IconFlipOrientation, IconFolderOpen, IconLeftPanelClosed, IconMinus, IconPlus } from "@synthesis/shared-icons";
import { Button } from "@synthesis/shared-ui/Button";

import { PREVIEW_HEIGHT_PX, PREVIEW_WIDTH_PX, SCALE_PERCENT } from "./modal-open-pdf.lib";

type PdfMinimapViewport = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type PdfMinimapProps = {
  mode?: "default" | "define";
  isLeftPanelHidden: boolean;
  rotation: number;
  scale: number;
  viewport: PdfMinimapViewport;
  previewSrc?: string;
  onToggleLeftPanel: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotate: () => void;
  onMinimapMouseDown: (event: ReactMouseEvent) => void;
};

const PdfMinimap = ({
  mode,
  isLeftPanelHidden,
  rotation,
  scale,
  viewport,
  previewSrc,
  onToggleLeftPanel,
  onZoomIn,
  onZoomOut,
  onRotate,
  onMinimapMouseDown,
}: PdfMinimapProps) => (
  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex w-full shrink-0 items-center gap-3 px-4 py-3">
    <div className="pointer-events-auto flex flex-col items-center gap-1">
      <span className="text-type-minor bg-neutrals-95/50 min-w-[3rem] rounded p-1 text-sm tabular-nums">{Math.round(scale * SCALE_PERCENT)}%</span>
      {mode !== "define" && (
        <Button
          size="sm"
          leftIcon={isLeftPanelHidden ? <IconFolderOpen className="size-4" /> : <IconLeftPanelClosed className="size-5" />}
          onClick={onToggleLeftPanel}
        />
      )}
      <Button size="sm" theme="elevated" leftIcon={<IconPlus className="size-5" />} onClick={onZoomIn} />
      <Button size="sm" theme="elevated" leftIcon={<IconMinus className="size-5" />} onClick={onZoomOut} />
    </div>
    <div className="bg-neutrals-95 pointer-events-auto">
      <div
        className="border-neutrals-90 relative cursor-pointer overflow-hidden rounded-md border-[2px]"
        style={{ height: PREVIEW_HEIGHT_PX, width: PREVIEW_WIDTH_PX }}
        onMouseDown={onMinimapMouseDown}
      >
        <div className="absolute inset-0" style={{ transform: `rotate(${rotation}deg)` }}>
          {previewSrc ? <img src={previewSrc} alt="minimap" className="h-full w-full object-contain" /> : null}
          <div
            style={{
              width: viewport.width,
              height: viewport.height,
              left: viewport.left,
              top: viewport.top,
            }}
            className="border-tertiary-80 bg-tertiary-80/10 pointer-events-none absolute border-2"
          />
        </div>
      </div>
    </div>
    <div className="pointer-events-auto flex h-full items-end">
      <Button size="sm" theme="elevated" leftIcon={<IconFlipOrientation className="size-5" />} onClick={onRotate} />
    </div>
  </div>
);

export default PdfMinimap;
